import { EditOutlined, EyeOutlined } from '@ant-design/icons'
import { SevenroomsIcon } from '@components/third-party/icons/Icons'
import { User, UserProviderProfile } from '@models/User'
import { updateUserProviderProfile } from '@services/UserService'
import {
  Card as ANTDCard,
  App,
  Avatar,
  Button,
  Card,
  Collapse,
  Descriptions,
  Flex,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import dayjs from 'dayjs'
import { useState } from 'react'
import { Link as ReactRouterLink } from 'react-router-dom'
import { DEFAULT_ERROR, DEFAULT_SUCCESS } from '../../App'

const { Text, Link } = Typography

interface Props {
  profile: UserProviderProfile
  user: User
}
export const UserProviderCard = ({ profile, user }: Props) => {
  const { message } = App.useApp()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [editing, setEditing] = useState<boolean>(false)
  const [providerUserId, setProviderUserId] = useState<
    UserProviderProfile['providerUserId']
  >(profile.providerUserId)

  return (
    <ANTDCard styles={{ body: { padding: 0 } }}>
      <Space className="w-full space-between" orientation="vertical">
        <Flex vertical>
          <Collapse
            ghost
            collapsible="icon"
            defaultActiveKey={[]}
            expandIconPlacement="end"
            expandIcon={() => (
              <Button variant="filled" color="default" icon={<EyeOutlined />}>
                More infos
              </Button>
            )}
            items={[
              {
                key: '1',
                label: (
                  <Flex justify="space-between" align="center">
                    <Flex align="center" gap={8}>
                      <Avatar
                        shape="square"
                        size={30}
                        style={{ background: '#000' }}
                        icon={
                          <SevenroomsIcon
                            width={20}
                            height={20}
                            color={'#fff'}
                          />
                        }
                      />

                      <Text strong>Sevenrooms</Text>
                      <Tag variant="outlined">{profile.providerType?.name}</Tag>
                    </Flex>
                  </Flex>
                ),
                children: (
                  <Card>
                    <Descriptions
                      key={profile.id}
                      column={2}
                      layout="vertical"
                      size="small"
                    >
                      <Descriptions.Item span={2} label="Provider Name">
                        {profile.providerType?.name}
                      </Descriptions.Item>
                      <Descriptions.Item
                        span={2}
                        label={
                          <Space size={5}>
                            <Text type="secondary">Provider ID</Text>
                            <Text
                              copyable={{
                                text: String(profile.providerId),
                              }}
                            />
                          </Space>
                        }
                      >
                        <Text>{profile.providerId}</Text>
                      </Descriptions.Item>
                      <Descriptions.Item
                        span={2}
                        label={
                          <Space size={5}>
                            <Text type="secondary">Provider User ID</Text>
                            <Text
                              copyable={{
                                text: String(profile.providerUserId),
                              }}
                            />
                            <Link onClick={() => setEditing(!editing)}>
                              <EditOutlined />
                            </Link>
                          </Space>
                        }
                      >
                        <Text
                          style={{ width: '100%' }}
                          disabled={isLoading}
                          editable={{
                            editing: editing,
                            icon: <></>,
                            onEnd: () => setEditing(false),
                            onChange: (value) => {
                              setIsLoading(true)
                              updateUserProviderProfile(
                                user.id,
                                profile.id,
                                value
                              )
                                .then(() => {
                                  message.success(DEFAULT_SUCCESS)
                                  setProviderUserId(value)
                                })
                                .catch(() => {
                                  message.error(DEFAULT_ERROR)
                                })
                                .finally(() => {
                                  setIsLoading(false)
                                })
                            },
                          }}
                        >
                          {providerUserId}
                        </Text>
                      </Descriptions.Item>
                      <Descriptions.Item span={1} label="Marketing opt-in">
                        <Tooltip
                          title={
                            profile.marketingOptinAt
                              ? dayjs(profile.marketingOptinAt).format(
                                  'YYYY-MM-DD HH:mm Z'
                                )
                              : undefined
                          }
                        >
                          {profile.marketingOptinAt
                            ? dayjs(profile.marketingOptinAt).format(
                                'DD/MM/YYYY'
                              )
                            : '--'}
                        </Tooltip>
                      </Descriptions.Item>
                      <Descriptions.Item span={1} label="Last interaction">
                        <Tooltip
                          title={
                            profile.lastInteractionAt
                              ? dayjs(profile.lastInteractionAt).format(
                                  'YYYY-MM-DD HH:mm Z'
                                )
                              : undefined
                          }
                        >
                          {profile.lastInteractionAt
                            ? dayjs(profile.lastInteractionAt).format(
                                'DD/MM/YYYY'
                              )
                            : '--'}
                        </Tooltip>
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                ),
              },
            ]}
          />
          <Flex className="w-full" style={{ padding: 12 }}>
            <ReactRouterLink
              className="w-full"
              to={profile.providerType?.managerBaseUrl + profile.providerUserId}
              target="_blank"
            >
              <Button block type="primary" disabled={!profile.providerUserId}>
                View on SevenRooms
              </Button>
            </ReactRouterLink>
          </Flex>
        </Flex>
      </Space>
    </ANTDCard>
  )
}

export type UserProviderCardType = {
  ProviderCard: typeof UserProviderCard
}
